import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Share2,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  MessageCircle,
  Video,
  Camera,
  Heart,
  UserCheck,
  Megaphone,
} from "lucide-react";

export const Route = createFileRoute("/services_/social-media-marketing")({
  head: () => ({
    meta: [
      {
        title:
          "Social Media Marketing Services — KMWAdvertising · Organic & Paid Social",
      },
      {
        name: "description",
        content:
          "Social Media Marketing Services That Build Brands, Engage Audiences and Drive Business Growth. Strategy, Content Creation, Paid Social & Influencer Marketing by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Social Media Marketing Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Transform Conversations into Customers with Strategic Social Media Marketing. Management across Facebook, Instagram, LinkedIn, X, YouTube, Threads & TikTok.",
      },
    ],
  }),
  component: SocialMediaMarketingServicePage,
});

export function SocialMediaMarketingServicePage() {
  const whySocialMatters = [
    {
      title: "Increase Brand Awareness",
      desc: "Reach new, targeted audiences through platform-specific storytelling, high-performing visual content, and targeted campaigns.",
      icon: Megaphone,
    },
    {
      title: "Strengthen Relationships",
      desc: "Build trust and community loyalty through active conversations, timely responses, and authentic brand interactions.",
      icon: Heart,
    },
    {
      title: "Generate Leads & Sales",
      desc: "Drive customer acquisition through integrated organic funnel content and high-ROAS paid social advertising campaigns.",
      icon: Target,
    },
    {
      title: "Improve Brand Credibility",
      desc: "Publishing regular, expert content reinforces brand values, showcases customer proof, and enhances market authority.",
      icon: Award,
    },
    {
      title: "Actionable Audience Insights",
      desc: "Gather demographic, interest, and engagement data to continuously refine messaging and improve digital marketing ROI.",
      icon: BarChart3,
    },
  ];

  const socialStrategyPoints = [
    "Commercial goal alignment & KPI definition",
    "Deep audience demographic & interest research",
    "Competitor social media strategy audit",
    "Platform mix selection (FB, IG, LinkedIn, X, YT, Threads)",
    "Content pillars & brand tone-of-voice framework",
    "Monthly content calendar & approval workflows",
    "Hashtag architecture & trend monitoring",
    "Paid social budget & funnel allocation",
  ];

  const contentCreationSuite = [
    {
      title: "Social Media Design & Graphics",
      desc: "Custom branded static posts, carousel graphics, and infographic visual assets.",
      icon: Camera,
    },
    {
      title: "Reels & Short Video Production",
      desc: "Engaging 9:16 vertical video Reels, Shorts, and TikTok clips optimized for algorithm reach.",
      icon: Video,
    },
    {
      title: "Motion Graphics & Animation",
      desc: "Eye-catching animated ads and motion graphics that capture scroll attention.",
      icon: Sparkles,
    },
    {
      title: "Copywriting & Storytelling",
      desc: "Persuasive post captions, hooks, calls-to-action, and brand storytelling narratives.",
      icon: FileCheck,
    },
  ];

  const paidSocialPlatforms = [
    "Facebook Ads (Feed, Stories, Reels, Marketplace)",
    "Instagram Ads (Feed, Stories, Reels, Explore)",
    "LinkedIn B2B Advertising (Sponsored Content, Lead Forms)",
    "X (Twitter) Ads (Promoted Tweets, Trend Takeovers)",
    "Pinterest Shopping & Visual Ads",
    "Snapchat AR & Story Commercials",
    "YouTube In-Stream & Shorts Video Ads",
  ];

  const communityManagementTasks = [
    "24/7 Monitoring of post comments & direct messages",
    "Prompt customer service & inquiry coordination",
    "Fostering active brand advocate discussions",
    "User-Generated Content (UGC) curation",
    "Brand sentiment monitoring & escalation rules",
    "Online reputation protection & crisis guidance",
  ];

  const influencerMarketingServices = [
    "Niche & Micro-Influencer Identification",
    "Campaign Brief Development & Selection",
    "Content Co-Creation & Approval Workflows",
    "Contractual & Regulatory Compliance Guidance",
    "Tracked Link & Promo Code Performance",
    "Comprehensive Influencer ROI Measurement",
  ];

  const socialPlatformsManaged = [
    "Facebook",
    "Instagram",
    "LinkedIn",
    "X (Twitter)",
    "YouTube",
    "Pinterest",
    "Snapchat",
    "Threads",
  ];

  const analyticsMetrics = [
    "Total Reach & Impression Delivery",
    "Audience Engagement Rate (%)",
    "Net Follower & Community Growth",
    "Link Click-Through Rate (CTR)",
    "Video Retention & 3s/10s Views",
    "Website Referral Traffic & Leads",
    "Paid Ad Return on Ad Spend (ROAS)",
    "Competitor Share of Voice (SOV)",
  ];

  const kmwaStrengths = [
    "25+ years of strategic advertising & media planning heritage",
    "Dedicated team of social strategists, video editors & graphic designers",
    "Platform-specific algorithm & creative formatting expertise",
    "Seamless integration of organic community & performance paid ads",
    "AI-assisted social listening & sentiment tracking tools",
    "Transparent reporting with clear ROI and lead attribution",
    "Active community management & crisis reputation support",
    "Influencer campaign management & ROI tracking",
    "Cross-channel digital marketing alignment",
    "Conversion-focused growth strategy",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Social Media Marketing</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Share2 className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Social Media Marketing Services That Build Brands, Engage Audiences and Drive Business Growth
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Organic Social Management & Paid Social Media Advertising
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Transform Conversations into Customers with Strategic Social Media Marketing
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Social media has evolved far beyond a platform for sharing updates. Today, it is one of the most influential marketing channels for building brand awareness, engaging customers, generating leads and driving measurable business growth across Facebook, Instagram, LinkedIn, X, YouTube, Pinterest, Snapchat, and Threads.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Success on social media demands more than simply posting content. It requires a well-defined strategy, compelling creative, community engagement, and performance-driven paid social advertising.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we deliver end-to-end Social Media Marketing (SMM) services combining 25+ years of advertising heritage with cutting-edge digital expertise to convert social audiences into loyal brand advocates.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Social Media Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY SOCIAL MEDIA MARKETING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Brand Connection & Growth
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Social Media Marketing Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Billions of active users engage on social networks daily. Strategic social marketing positions your brand where target customers spend their time.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whySocialMatters.map((item, idx) => {
                const SIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <SIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* STRATEGY, MANAGEMENT & CONTENT CREATION */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                End-to-End Social Execution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Social Media Marketing Services
              </h2>
            </div>

            {/* STRATEGY & MANAGEMENT GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* STRATEGY */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Target className="h-3.5 w-3.5" />
                    Strategic Growth Roadmap
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Social Media Strategy</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Build a Clear Roadmap for Sustainable Audience Growth
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We align social media planning with overall business goals through deep demographic research, competitor audits, and content calendars.
                </p>

                <div className="space-y-2 pt-2">
                  {socialStrategyPoints.map((pt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* PAID SOCIAL ADVERTISING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <Share2 className="h-3.5 w-3.5" />
                    High-ROAS Paid Social
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Paid Social Media Advertising</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Reach Highly Targeted Audiences Across Platforms
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Precision paid social ad campaigns engineered to generate leads, drive e-commerce sales, and scale customer acquisition.
                </p>

                <div className="space-y-2 pt-2">
                  {paidSocialPlatforms.map((psp, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{psp}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CONTENT CREATION & CREATIVE SUITE */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Content Creation & Creative Design
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  High-quality, scroll-stopping creative design engineered to inspire audience action and build brand equity.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {contentCreationSuite.map((suite, idx) => {
                  const CIcon = suite.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <CIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{suite.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{suite.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* COMMUNITY & INFLUENCER MARKETING */}
            <div className="grid lg:grid-cols-12 gap-8 items-start mb-16">
              {/* Community Management */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-[var(--glow-purple)]" />
                  Community Management & Moderation
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Turning followers into loyal brand advocates through active engagement and responsive support:
                </p>
                <div className="space-y-2 pt-2">
                  {communityManagementTasks.map((cmt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{cmt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Influencer Marketing */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-sky-400" />
                  Influencer Marketing & Collaborations
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Collaborating with trusted creator voices to expand reach, build authority, and drive authentic conversions:
                </p>
                <div className="space-y-2 pt-2">
                  {influencerMarketingServices.map((ims, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{ims}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* PLATFORMS & ANALYTICS */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Platforms */}
              <div className="lg:col-span-5 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white">Platforms We Manage</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Platform-native content formats and algorithms tailored for every key social channel:
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {socialPlatformsManaged.map((spm, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium text-center">
                      {spm}
                    </div>
                  ))}
                </div>
              </div>

              {/* Analytics */}
              <div className="lg:col-span-7 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-amber-400" />
                  Analytics, Benchmarking & Reporting
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Data-driven decision making and regular performance dashboards tracking real business impact:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {analyticsMetrics.map((am, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                      <span>{am}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Advertising Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Share2 className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              Social Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Grow Your Brand Through Social Media?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our social media experts today for a tailored strategy that combines creative storytelling, audience insights, and performance-driven execution.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Social Media Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
